import React, { memo, FC } from "react";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Choices from "../Choices";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ReusableAccordian from "../ReusableAccordion";
import PleaseNote from "../PleaseNote";
import { Image } from "cloudinary-react";
import {
  withKey,
  specialOrder,
  withHandle,
  handleColors,
  secondaryHandle,
} from "./defaults";
import Video from "../Video";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useHandleSelector from "./useHandleSelector";
import { addScrollElements } from "../AccordionWrapper/AccordianWrapper";

const ColLayoutSpacing = styled("div")(({ theme }) => ({
  marginBottom: 10,
  minHeight: 150,
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-start",
  flexDirection: "column",
  padding: theme.spacing(1, 2),
  borderLeft: `2px solid ${theme.palette.divider}`,
}));

interface HandleAccordionProps {
  hideHandles: boolean;
}

const HandleAccordian: FC<HandleAccordionProps> = ({ hideHandles }) => {
  const {
    handleImg,
    handleClickHandleStyle,
    handleClickWithKey,
    handleClickWithHandle,
    handleClose,
    handleOpen,
    handleChange,
    handleColor,
    finish,
    open,
    withHandleChoice,
    withKeyChoice,
    keyChoiceOptions,
    handleStyle,
    doorType,
    bluetoothHandleWifi,
    handleWifiChange,
    stilesAndRails,
    hasBluetoothHandle,
    singleSidedOdd,
    handleOptions,
    isPico,
    secondaryHandleChoice,
    handleClickSecondaryHandle,
    isSecondaryHandleAvailable,
  } = useHandleSelector(hideHandles);

  return (
    <ReusableAccordian
      accordionName="handle"
      heading="Handle Set"
      secondaryHeading="Select handle combination"
      colOrRow="col"
    >
      {withHandleChoice === "No Handle Set" ? (
        <ColLayout>
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            Guts Only
          </Typography>
          {isPico ? (
            <Typography variant="subtitle1">Setback is 25mm</Typography>
          ) : stilesAndRails?.includes("Slimline") ||
            stilesAndRails?.includes("Euro") ? (
            <Typography variant="subtitle1">Setback is 35mm</Typography>
          ) : stilesAndRails?.includes("Bottom") ? (
            <Typography variant="subtitle1">Setback is 45mm</Typography>
          ) : null}

          {!isPico ? (
            <>
              <br />
              <Typography variant="subtitle1">
                Works with: Emtek Configuration 5
              </Typography>
            </>
          ) : singleSidedOdd ? (
            <PleaseNote
              title="Please Note"
              notes="Handle must have an offset, and the thumbturn needs to be single-sided to prevent interference with the jamb leg"
            />
          ) : null}
        </ColLayout>
      ) : null}

      {(withHandleChoice === "No Handle Set" || !withHandleChoice) &&
      doorType !== "Slider" ? (
        <RowLayout>
          {withHandle.map((state) => (
            <div
              key={state.id}
              onClick={() => handleClickWithHandle(state.label)}
            >
              <Choices
                label={state.label}
                variant={
                  state.label === withHandleChoice ? "filled" : "outlined"
                }
                checkMark={state.label === withHandleChoice}
              />
            </div>
          ))}
        </RowLayout>
      ) : (
        <>
          {doorType !== "Slider" && (
            <ColLayout>
              <RowLayout>
                {withHandle.map((state) => (
                  <div
                    key={state.id}
                    onClick={() => handleClickWithHandle(state.label)}
                  >
                    <Choices
                      label={state.label}
                      variant={
                        state.label === withHandleChoice ? "filled" : "outlined"
                      }
                      checkMark={state.label === withHandleChoice}
                    />
                  </div>
                ))}
              </RowLayout>
              <Divider style={{ width: "150%", margin: 10 }} />
            </ColLayout>
          )}

          <PleaseNote
            notes={`${isPico ? "Alpine Frost" : "Satin Nickel"} finish recommended for
            jobsite within 3 miles of coast`}
          />

          <RowLayout style={{ alignItems: "flex-start" }}>
            <ColLayoutSpacing style={{ borderLeft: "none" }}>
              <Typography variant="subtitle1" style={{ marginBottom: 10 }}>
                With Key?<span style={{ color: "red", fontSize: 22 }}>*</span>
              </Typography>
              <RowLayout>
                {keyChoiceOptions.map((state) => (
                  <div
                    key={state.id}
                    onClick={() => handleClickWithKey(state.label)}
                  >
                    <Choices
                      disabled={keyChoiceOptions.length === 1}
                      label={state.label}
                      variant={
                        state.label === withKeyChoice ? "filled" : "outlined"
                      }
                      checkMark={state.label === withKeyChoice}
                    />
                  </div>
                ))}
              </RowLayout>
            </ColLayoutSpacing>

            <ColLayoutSpacing>
              <Typography variant="subtitle1" style={{ marginBottom: 10 }}>
                Handle Style
                <span style={{ color: "red", fontSize: 22 }}>*</span>
              </Typography>

              <RowLayout>
                {handleOptions.map((state) => (
                  <div
                    key={state.id}
                    onClick={() => handleClickHandleStyle(state.label)}
                  >
                    <Choices
                      label={state.label}
                      variant={
                        state.label === handleStyle ? "filled" : "outlined"
                      }
                      checkMark={state.label === handleStyle}
                    />
                  </div>
                ))}
              </RowLayout>
            </ColLayoutSpacing>

            {doorType !== "Slider" && !isPico && (
              <ColLayoutSpacing>
                <Typography style={{ paddingRight: 10 }}>
                  Special Orders:
                </Typography>
                <Typography variant="subtitle2" style={{ marginBottom: 20 }}>
                  (* Up-charge applies to all special orders)
                </Typography>

                <RowLayout style={{ padding: 5, margin: 0 }}>
                  {specialOrder.slice(0, 4).map((state) => (
                    <div
                      key={state.id}
                      onClick={() => handleClickHandleStyle(state.label)}
                    >
                      <Choices
                        label={state.label}
                        variant={
                          state.label === handleStyle ? "filled" : "outlined"
                        }
                        checkMark={state.label === handleStyle}
                      />
                    </div>
                  ))}
                </RowLayout>

                <RowLayout style={{ padding: 5, margin: 0 }}>
                  {specialOrder.slice(4).map((state) => (
                    <div
                      key={state.id}
                      onClick={() => handleClickHandleStyle(state.label)}
                      style={{
                        marginRight: state.colorCategory === "emtek" ? 10 : 0,
                      }}
                    >
                      <Choices
                        label={state.label}
                        variant={
                          state.label === handleStyle ? "filled" : "outlined"
                        }
                        checkMark={state.label === handleStyle}
                      />
                    </div>
                  ))}
                </RowLayout>
              </ColLayoutSpacing>
            )}
          </RowLayout>

          <ColLayout style={{ marginTop: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                // width: "100%",
              }}
            >
              {handleStyle && handleImg?.label && (
                <ColLayout style={{ marginRight: 40, marginBottom: 0 }}>
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId={handleImg.imgLabel}
                  />
                </ColLayout>
              )}

              {hasBluetoothHandle && (
                <RowLayout
                  style={{
                    paddingRight: 20,
                    marginBottom: 0,
                    alignItems: "flex-end",
                  }}
                >
                  <ColLayout
                    style={{ marginLeft: 20, marginRight: 40, marginBottom: 0 }}
                  >
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={"Handles/nouvel_lever"}
                    />
                    <Typography style={{ paddingTop: 5 }} variant="subtitle2">
                      Exterior/Interior Nouvel Handle
                    </Typography>
                  </ColLayout>

                  <ColLayout
                    style={{ marginLeft: 20, marginRight: 40, marginBottom: 0 }}
                  >
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={"Handles/bt_deadbolt_nofinish"}
                    />
                    <Typography style={{ paddingTop: 5 }} variant="subtitle2">
                      Smart Lock
                    </Typography>
                  </ColLayout>
                </RowLayout>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                {hasBluetoothHandle && (
                  <>
                    <RowLayout style={{ margin: 10 }}>
                      <FormControl
                        variant="outlined"
                        style={{ paddingRight: 20, alignItems: "flex-start" }}
                        error={!bluetoothHandleWifi ? true : false}
                      >
                        <InputLabel>
                          Smart Lock Type<span style={{ color: "red" }}>*</span>
                        </InputLabel>
                        <Select
                          style={{ width: 250, height: 50 }}
                          label="Smart Lock Type"
                          value={bluetoothHandleWifi}
                          onChange={handleWifiChange}
                        >
                          <MenuItem value=""></MenuItem>
                          <MenuItem value="Bluetooth">Bluetooth</MenuItem>
                          <MenuItem value="Bluetooth & Wi-Fi">
                            Bluetooth & Wi-Fi
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </RowLayout>
                  </>
                )}

                <RowLayout style={{ margin: 10 }}>
                  <FormControl
                    variant="outlined"
                    error={!finish ? true : false}
                  >
                    <InputLabel id="demo-controlled-open-select-label">
                      Select Finish<span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <Select
                      style={{ width: 250, height: 50 }}
                      labelId="demo-controlled-open-select-label"
                      id="demo-controlled-open-select"
                      label="Select Finish"
                      open={open}
                      onClose={handleClose}
                      onOpen={handleOpen}
                      value={finish || ""}
                      onChange={handleChange}
                      required
                      renderValue={(selected) =>
                        selected ? String(selected) : <em>None</em>
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {handleColor?.length ? (
                        handleColor.map((color) => (
                          <MenuItem key={color} value={color}>
                            {color}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value=""></MenuItem>
                      )}
                    </Select>
                  </FormControl>

                  {finish &&
                    handleColors
                      .filter((color) => color.label === finish)
                      .map((item) => (
                        <Image
                          key={item.label}
                          style={{ paddingLeft: 40 }}
                          cloudName="ag-millworks"
                          secure="true"
                          publicId={item.img}
                        />
                      ))}
                </RowLayout>
              </div>
            </div>

            {handleStyle && withHandleChoice === "Add Handle Set" && (
              <Typography
                style={{ paddingTop: 40, alignSelf: "center" }}
                variant="subtitle2"
              >
                * All Images are depicted without exterior key or handle finish
              </Typography>
            )}

            {addScrollElements([
              "Secondary Handle",
            ])}

            {isSecondaryHandleAvailable && (
              <ColLayout style={{ width: "100%", marginTop: 14 }}>
                <Divider style={{ width: "100%", marginBottom: 9 }} />
                <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                  Handle on Secondary Door (Upcharge Applies)
                  <span style={{ color: "red", fontSize: 22 }}>*</span>
                </Typography>
                <RowLayout>
                  {secondaryHandle.map((state) => (
                    <div
                      key={state.id}
                      onClick={() =>
                        handleClickSecondaryHandle(state.label)
                      }
                    >
                      <Choices
                        label={state.label}
                        variant={
                          state.label === secondaryHandleChoice
                            ? "filled"
                            : "outlined"
                        }
                        checkMark={state.label === secondaryHandleChoice}
                      />
                    </div>
                  ))}
                </RowLayout>
                <Typography variant="subtitle2">
                  The secondary door operates using an edge-mounted flush bolt.
                </Typography>
                <Typography variant="subtitle2">
                  The optional dummy handle is non-operating and matches the
                  primary handle's style and finish.
                </Typography>
              </ColLayout>
            )}

            {hasBluetoothHandle && (
              <>
                <Typography
                  style={{
                    paddingTop: 10,
                    fontWeight: "bold",
                    alignSelf: "flex-start",
                  }}
                  variant="subtitle2"
                >
                  * Lock stile will be 4-3/4" with single point smart lock
                </Typography>
                <Typography
                  style={{ paddingTop: 10, alignSelf: "flex-start" }}
                  variant="subtitle2"
                >
                  * Access can be granted using a fingerprint scan, touch
                  keypad, or the Yale access app
                </Typography>
              </>
            )}
          </ColLayout>
        </>
      )}

      {doorType?.includes("Bi-fold") &&
        (handleStyle === "Verona" || handleStyle === "Dallas") &&
        !isPico &&
        withHandleChoice === "Add Handle Set" && (
          <Video src="https://www.youtube.com/watch?v=rPALQ4sW8MA" />
        )}
    </ReusableAccordian>
  );
};

export default memo(HandleAccordian);
